import React, { useCallback } from 'react';
import keycode from 'keycode';
import Heading from '@atlaskit/heading';
import { Inline, xcss, Box, Pressable } from '@atlaskit/primitives';
import BoardIcon from '@atlaskit/icon/core/board';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useShowBoardName } from '@atlassian/jira-software-view-settings/src/controllers/index.tsx';
import { useBoardId } from '../../../../../common/hooks/use-board-id/index.tsx';
import { useIsCMPBacklog } from '../../../../../state/state-hooks/capabilities.tsx';
import { useBacklogDispatch } from '../../../../../state/index.tsx';
import { sprintBoardLinkClickRequest } from '../../../../../state/actions/sprints/board-link-click/index.tsx';
import messages from './messages.tsx';

const BaseHeading = ({ listName }: { listName: string }) => {
	return (
		<Heading as="h2" size="xsmall">
			{listName}
		</Heading>
	);
};

export const CardListHeading = ({
	rapidViewId,
	boardName,
	listName,
	isBacklog,
}: {
	rapidViewId?: number;
	boardName?: string;
	listName: string;
	isBacklog: boolean;
}) => {
	const { formatMessage } = useIntl();
	const boardId = useBoardId();
	const isCMP = useIsCMPBacklog();
	const dispatch = useBacklogDispatch();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const showBoardName = useShowBoardName();

	const onEnterLink = useCallback(
		(analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'clicked sprintOriginBoardLink');

			if (!rapidViewId) {
				return;
			}

			const action = sprintBoardLinkClickRequest(rapidViewId, analyticsEvent);

			dispatch(action);
		},
		[rapidViewId, dispatch],
	);

	const onClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => {
			event.stopPropagation();
			event.preventDefault();
			const analyticsEvent = createAnalyticsEvent({});

			onEnterLink(analyticsEvent);
		},
		[createAnalyticsEvent, onEnterLink],
	);

	const onKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = useCallback(
		(event) => {
			if (event.keyCode === keycode('enter') || event.keyCode === keycode('space')) {
				event.stopPropagation();
				event.preventDefault();
				const analyticsEvent = createAnalyticsEvent({});
				onEnterLink(analyticsEvent);
			}
		},
		[onEnterLink, createAnalyticsEvent],
	);

	const getBoardLink = useCallback(
		(externalBoardName?: string) => {
			if (externalBoardName === undefined) {
				return (
					<Inline alignBlock="center" space="space.050">
						<BoardIcon label={formatMessage(messages.boardIconLabel)} />
						{formatMessage(messages.restrictedBoard)}
					</Inline>
				);
			}

			return (
				<Pressable
					role="link"
					padding="space.0"
					xcss={LinkButtonStyles}
					onClick={onClick}
					onKeyDown={onKeyDown}
				>
					<Inline alignBlock="center" space="space.050">
						<BoardIcon label={formatMessage(messages.boardIconLabel)} />
						<Box xcss={LinkStyles}>
							{externalBoardName ?? formatMessage(messages.restrictedBoard)}
						</Box>
					</Inline>
				</Pressable>
			);
		},
		[formatMessage, onClick, onKeyDown],
	);

	if (!isCMP || !showBoardName || !rapidViewId || rapidViewId === boardId || isBacklog) {
		return <BaseHeading listName={listName} />;
	}

	return (
		<Inline alignBlock="center" space="space.100">
			<BaseHeading listName={listName} />
			<FormattedMessage
				{...messages.originBoardLink}
				values={{
					boardName: getBoardLink(boardName),
				}}
			/>
		</Inline>
	);
};

const LinkStyles = xcss({
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	maxWidth: '150px',
	display: 'inline-block',
});

const LinkButtonStyles = xcss({
	textDecoration: 'underline',
	background: 'none',
	border: 'none',
	color: 'color.link',
	':hover': {
		color: 'color.link',
		textDecoration: 'none',
	},
	':active': {
		color: 'color.link.pressed',
		textDecoration: 'none',
	},
	':focus': {
		color: 'color.link',
		textDecoration: 'underline',
	},
});
